/**
 * Content-level Search Anchor Registry.
 *
 * Defines individual settings and controls that live INSIDE navigable sections.
 * Each anchor maps to a specific hash-link so the search engine can surface
 * granular items (e.g., "Redis Cache" or "Maintenance Mode") that navigate
 * directly to the containing SettingsSection.
 *
 * These items are merged into the searchable items list alongside nav-config
 * items, inheriting permission gating from their parent section.
 */

import type { SearchMeta } from "./searchIndex.js";

export type SearchAnchor = {
  /** Unique ID for this content item (prefixed with `content-` to avoid collisions) */
  id: string;
  /** Display label shown in search results */
  label: string;
  /** Navigation path including hash anchor (e.g., "/settings/general#feature-flags") */
  path: string;
  /** Icon for search result display */
  icon: string;
  /** Parent section ID (matches navConfig section ID for permission inheritance) */
  sectionId: string;
  /** Parent section label for breadcrumb display */
  sectionLabel: string;
  /** Parent group label(s) for breadcrumb (e.g., "General Settings > Feature Flags") */
  groupLabel: string;
  /** Admin-only gating (matches parent section) */
  adminOnly?: boolean;
  /** Feature flag gating */
  requiresFeature?: "wsdashboard" | "webconsole";
};

/**
 * Content-level search metadata, keyed by SearchAnchor.id.
 * Follows the same shape as the nav-level searchMetadata.
 */
export const contentSearchMetadata: Record<string, SearchMeta> = {
  // ── General Settings > Instance Settings ────────────────────────────
  "content-instance-name": {
    description: "The display name for your QuickBox Pro instance",
    keywords: ["instance", "name", "title", "quickbox"]
  },
  "content-admin-email": {
    description: "Contact email for system notifications",
    keywords: ["admin", "email", "contact", "notifications"]
  },
  "content-web-root": {
    description: "Base URL for dashboard access (auto-detected if empty)",
    keywords: ["web root", "base url", "hostname", "domain"]
  },

  // ── General Settings > Timezone & Locale ────────────────────────────
  "content-timezone": {
    description: "System timezone for date and time display",
    keywords: ["timezone", "time zone", "utc", "region"]
  },
  "content-date-format": {
    description: "Format used for displaying dates throughout the dashboard",
    keywords: ["date", "format", "display", "iso"]
  },

  // ── General Settings > Display Preferences ──────────────────────────
  "content-default-page": {
    description: "Landing page shown after login for regular users",
    keywords: ["default", "landing", "page", "redirect", "login"]
  },
  "content-admin-default-page": {
    description: "Landing page shown after login for admin users",
    keywords: ["admin", "default", "landing", "page", "redirect"]
  },
  "content-default-theme": {
    description: "Theme applied to new user accounts by default",
    keywords: ["theme", "default", "dark", "light", "appearance"]
  },

  // ── General Settings > Additional Dashboard Settings ────────────────
  "content-disk-mount": {
    description: "Filesystem mount point for disk usage monitoring",
    keywords: ["disk", "mount", "space", "filesystem", "usage", "storage"]
  },
  "content-network-interface": {
    description: "Network interface used for bandwidth statistics",
    keywords: ["network", "interface", "eth0", "nic", "bandwidth", "adapter"]
  },
  "content-vnstat": {
    description: "vnStat bandwidth monitoring configuration",
    keywords: ["vnstat", "bandwidth", "traffic", "monitoring", "start date", "statistics"]
  },
  "content-redis-cache": {
    description: "Redis connection for caching and session storage",
    keywords: ["redis", "cache", "url", "host", "port", "password", "connection", "telemetry"]
  },

  // ── General Settings > Uploads ──────────────────────────────────────
  "content-avatar-max-size": {
    description: "Maximum file size for user avatar uploads",
    keywords: ["avatar", "max", "size", "upload", "kilobytes", "limit"]
  },
  "content-banner-max-size": {
    description: "Maximum file size for user banner uploads",
    keywords: ["banner", "max", "size", "upload", "kilobytes", "limit"]
  },
  "content-allow-avatar-upload": {
    description: "Toggle to let users upload custom profile avatars",
    keywords: ["avatar", "upload", "allow", "profile", "picture"]
  },

  // ── General Settings > Branding ─────────────────────────────────────
  "content-collapsed-logo": {
    description: "Small logo shown when sidebar is collapsed",
    keywords: ["collapsed", "logo", "sidebar", "icon", "small"]
  },
  "content-full-logo": {
    description: "Full logo shown when sidebar is expanded",
    keywords: ["full", "logo", "sidebar", "expanded", "brand"]
  },
  "content-favicon": {
    description: "Browser tab favicon for the dashboard",
    keywords: ["favicon", "icon", "tab", "browser", "bookmark"]
  },
  "content-default-avatar": {
    description: "Default avatar used for users without a custom avatar",
    keywords: ["default", "avatar", "profile", "placeholder", "picture"]
  },

  // ── General Settings > Auth Pages ───────────────────────────────────
  "content-auth-show-logo": {
    description: "Show or hide the logo on login and registration pages",
    keywords: ["logo", "login", "register", "auth", "show", "hide"]
  },
  "content-auth-login-message": {
    description: "Custom welcome message displayed on auth pages (Markdown supported)",
    keywords: ["login", "message", "welcome", "markdown", "custom", "auth"]
  },

  // ── General Settings > Feature Flags ────────────────────────────────
  "content-maintenance-mode": {
    description: "Show maintenance page to non-admin users",
    keywords: ["maintenance", "mode", "offline", "downtime", "unavailable"]
  },
  "content-user-registration": {
    description: "Allow or block new user registrations",
    keywords: ["registration", "signup", "register", "new users", "open", "closed"]
  },
  "content-notification-channels": {
    description: "Control external notification channel access for non-admin users",
    keywords: ["notification", "channels", "discord", "signal", "whatsapp", "external"]
  },
  "content-web-console": {
    description: "Browser-based terminal for admin server access",
    keywords: ["web console", "terminal", "shell", "bash", "ssh", "command line"]
  },
  "content-wsdashboard": {
    description: "Real-time streaming session monitor for Emby and Jellyfin",
    keywords: ["wsdashboard", "streaming", "monitor", "emby", "jellyfin", "media", "sessions"]
  },
  "content-playback-mirror": {
    description: "Mirror active playback sessions in read-only mode",
    keywords: ["playback", "mirror", "stream", "watch", "read-only", "ttl"]
  },

  // ── Session Settings > Timeouts ─────────────────────────────────────
  "content-user-timeout": {
    description: "Minutes of inactivity before a user session expires",
    keywords: ["user", "timeout", "inactivity", "session", "expire", "minutes"]
  },
  "content-guest-timeout": {
    description: "Minutes of inactivity before a guest session expires",
    keywords: ["guest", "timeout", "inactivity", "session", "expire", "minutes"]
  },

  // ── Session Settings > Persistence & Cookies ────────────────────────
  "content-remember-me": {
    description: "Duration for 'Remember me' cookies before expiry",
    keywords: ["remember", "me", "cookie", "duration", "days", "persistent"]
  },
  "content-auto-extend": {
    description: "Reset session timer on each request to keep active users logged in",
    keywords: ["auto-extend", "activity", "session", "persistent", "rolling", "refresh"]
  },

  // ── Session Settings > Concurrency ──────────────────────────────────
  "content-multiple-sessions": {
    description: "Allow or block simultaneous login sessions per user",
    keywords: ["multiple", "sessions", "simultaneous", "concurrent", "device", "revoke"]
  },

  // ── Session Settings > Session Operations ───────────────────────────
  "content-invalidate-sessions": {
    description: "Force all non-admin users to log in again",
    keywords: ["invalidate", "revoke", "force", "logout", "non-admin", "bulk"]
  },

  // ── Security Settings > Disallowed Usernames ────────────────────────
  "content-disallowed-usernames": {
    description: "Manage usernames blocked from registration and login",
    keywords: ["disallowed", "usernames", "blocked", "reserved", "registration", "blocklist"]
  },

  // ── Security Settings > Banned IPs ──────────────────────────────────
  "content-banned-ips": {
    description: "Block IP addresses with application-level and iptables enforcement",
    keywords: ["banned", "ip", "block", "firewall", "iptables", "address"]
  },

  // ── Security Settings > Network Enforcement ─────────────────────────
  "content-network-enforcement": {
    description: "Manage iptables-level IP/CIDR blocks via the QBX_ENFORCEMENT chain",
    keywords: ["network", "enforcement", "iptables", "cidr", "block", "qbx", "chain", "firewall"]
  },

  // ── Registration Settings > Activation ──────────────────────────────
  "content-activation-mode": {
    description: "How new accounts are activated after registration",
    keywords: ["activation", "mode", "email", "admin", "disabled", "approval", "verify"]
  },

  // ── Registration Settings > Quotas ──────────────────────────────────
  "content-default-quota": {
    description: "Default disk quota assigned to new user accounts",
    keywords: ["quota", "disk", "unlimited", "storage", "limit", "gb", "tb", "mb"]
  },

  // ── Registration Settings > Default Group ───────────────────────────
  "content-default-group": {
    description: "Default user group assigned to new registrations",
    keywords: ["default", "group", "registration", "assign", "access", "membership", "new user", "role"]
  },

  // ── Registration Settings > Username Policy ─────────────────────────
  "content-username-min-length": {
    description: "Minimum character length for usernames",
    keywords: ["username", "minimum", "length", "characters", "min"]
  },
  "content-username-max-length": {
    description: "Maximum character length for usernames",
    keywords: ["username", "maximum", "length", "characters", "max"]
  },
  "content-force-lowercase": {
    description: "Automatically convert usernames to lowercase during registration",
    keywords: ["lowercase", "username", "force", "case", "convert"]
  },
  "content-username-regex": {
    description: "Custom regex pattern for username validation",
    keywords: ["username", "regex", "pattern", "validation", "custom", "format"]
  },

  // ── Registration Settings > Password Policy ─────────────────────────
  "content-password-min-length": {
    description: "Minimum character length for passwords",
    keywords: ["password", "minimum", "length", "characters", "min"]
  },
  "content-password-max-length": {
    description: "Maximum character length for passwords",
    keywords: ["password", "maximum", "length", "characters", "max"]
  },

  // ── Registration Settings > Welcome Email ───────────────────────────
  "content-welcome-email": {
    description: "Send a welcome email to new users after registration",
    keywords: ["welcome", "email", "send", "new user", "registration", "notification"]
  },

  // ── Security Settings > Firewall Rules Tab ────────────────────────────
  "content-firewall-origin-filter": {
    description: "Filter firewall rules by origin: QuickBox, Fail2Ban, WireGuard, Geo Policy, or System",
    keywords: ["filter", "origin", "quickbox", "fail2ban", "wireguard", "geo", "system", "category"]
  },
  "content-firewall-bulk-delete": {
    description: "Select and bulk-remove QBX-managed firewall rules",
    keywords: ["bulk", "delete", "remove", "select", "clear", "rules", "mass"]
  },

  // ── Security Settings > Geo-Lock Tab ──────────────────────────────────
  "content-geo-lock-add-rule": {
    description: "Create geo-lock rules to restrict streaming access by country",
    keywords: ["add", "create", "rule", "geo", "lock", "country", "streaming", "restrict"]
  },
  "content-geo-lock-violations": {
    description: "View recent geo-lock violation events and blocked access attempts",
    keywords: ["violations", "blocked", "attempts", "denied", "events", "recent"]
  },

  // ── Security Settings > Geo-Block Tab ─────────────────────────────────
  "content-geo-block-countries": {
    description: "Select countries to block or allow in the geo-block policy",
    keywords: ["countries", "blocklist", "allowlist", "select", "add", "remove", "policy"]
  },
  "content-geo-block-enforcement": {
    description: "Apply or disable geo-block firewall enforcement",
    keywords: ["apply", "enforce", "activate", "disable", "firewall", "active", "rules"]
  },
  "content-geo-block-ip-test": {
    description: "Test whether a specific IP address would be allowed or blocked",
    keywords: ["test", "ip", "check", "verify", "allowed", "blocked", "address"]
  },
};

/**
 * Static registry of content-level search anchors.
 * Each entry maps a specific setting or control to a navigable hash-link.
 */
export const contentSearchAnchors: SearchAnchor[] = [
  // ── General Settings > Instance Settings ────────────────────────────
  {
    id: "content-instance-name",
    label: "Instance Name",
    path: "/settings/general#instance-settings",
    icon: "lucide:type",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Instance Settings",
    adminOnly: true
  },
  {
    id: "content-admin-email",
    label: "Admin Email",
    path: "/settings/general#instance-settings",
    icon: "lucide:mail",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Instance Settings",
    adminOnly: true
  },
  {
    id: "content-web-root",
    label: "Web Root",
    path: "/settings/general#instance-settings",
    icon: "lucide:globe",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Instance Settings",
    adminOnly: true
  },

  // ── General Settings > Timezone & Locale ────────────────────────────
  {
    id: "content-timezone",
    label: "System Timezone",
    path: "/settings/general#timezone-locale",
    icon: "lucide:clock",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Timezone & Locale",
    adminOnly: true
  },
  {
    id: "content-date-format",
    label: "Date Format",
    path: "/settings/general#timezone-locale",
    icon: "lucide:calendar",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Timezone & Locale",
    adminOnly: true
  },

  // ── General Settings > Display Preferences ──────────────────────────
  {
    id: "content-default-page",
    label: "User Default Page",
    path: "/settings/general#display-preferences",
    icon: "lucide:home",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Display Preferences",
    adminOnly: true
  },
  {
    id: "content-admin-default-page",
    label: "Admin Default Page",
    path: "/settings/general#display-preferences",
    icon: "lucide:shield",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Display Preferences",
    adminOnly: true
  },
  {
    id: "content-default-theme",
    label: "Default Theme",
    path: "/settings/general#display-preferences",
    icon: "lucide:palette",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Display Preferences",
    adminOnly: true
  },

  // ── General Settings > Additional Dashboard Settings ────────────────
  {
    id: "content-disk-mount",
    label: "Disk Space Monitor",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:hard-drive",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Additional Dashboard Settings",
    adminOnly: true
  },
  {
    id: "content-network-interface",
    label: "Network Interface",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:network",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Additional Dashboard Settings",
    adminOnly: true
  },
  {
    id: "content-vnstat",
    label: "vnStat Bandwidth",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:bar-chart",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Additional Dashboard Settings",
    adminOnly: true
  },
  {
    id: "content-redis-cache",
    label: "Redis Cache",
    path: "/settings/general#additional-dashboard-settings",
    icon: "lucide:database",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Additional Dashboard Settings",
    adminOnly: true
  },

  // ── General Settings > Uploads ──────────────────────────────────────
  {
    id: "content-avatar-max-size",
    label: "Avatar Max Size",
    path: "/settings/general#uploads",
    icon: "lucide:image",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Uploads",
    adminOnly: true
  },
  {
    id: "content-banner-max-size",
    label: "Banner Max Size",
    path: "/settings/general#uploads",
    icon: "lucide:image",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Uploads",
    adminOnly: true
  },
  {
    id: "content-allow-avatar-upload",
    label: "Allow Avatar Uploads",
    path: "/settings/general#uploads",
    icon: "lucide:upload",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Uploads",
    adminOnly: true
  },

  // ── General Settings > Branding ─────────────────────────────────────
  {
    id: "content-collapsed-logo",
    label: "Collapsed Logo",
    path: "/settings/general#branding",
    icon: "lucide:image",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Branding",
    adminOnly: true
  },
  {
    id: "content-full-logo",
    label: "Full Logo",
    path: "/settings/general#branding",
    icon: "lucide:image",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Branding",
    adminOnly: true
  },
  {
    id: "content-favicon",
    label: "Favicon",
    path: "/settings/general#branding",
    icon: "lucide:bookmark",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Branding",
    adminOnly: true
  },
  {
    id: "content-default-avatar",
    label: "Default Avatar",
    path: "/settings/general#branding",
    icon: "lucide:user",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Branding",
    adminOnly: true
  },

  // ── General Settings > Auth Pages ───────────────────────────────────
  {
    id: "content-auth-show-logo",
    label: "Auth Page Logo",
    path: "/settings/general#auth-pages",
    icon: "lucide:image",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Auth Pages",
    adminOnly: true
  },
  {
    id: "content-auth-login-message",
    label: "Login Message",
    path: "/settings/general#auth-pages",
    icon: "lucide:message-square",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Auth Pages",
    adminOnly: true
  },

  // ── General Settings > Feature Flags ────────────────────────────────
  {
    id: "content-maintenance-mode",
    label: "Maintenance Mode",
    path: "/settings/general#feature-flags",
    icon: "lucide:construction",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Feature Flags",
    adminOnly: true
  },
  {
    id: "content-user-registration",
    label: "User Registration Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:user-plus",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Feature Flags",
    adminOnly: true
  },
  {
    id: "content-notification-channels",
    label: "External Notification Channels",
    path: "/settings/general#feature-flags",
    icon: "lucide:bell-off",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Feature Flags",
    adminOnly: true
  },
  {
    id: "content-web-console",
    label: "Web Console Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:terminal",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Feature Flags",
    adminOnly: true
  },
  {
    id: "content-wsdashboard",
    label: "WSDashboard Toggle",
    path: "/settings/general#feature-flags",
    icon: "lucide:tv",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Feature Flags",
    adminOnly: true
  },
  {
    id: "content-playback-mirror",
    label: "Playback Mirror",
    path: "/settings/general#feature-flags",
    icon: "lucide:copy",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "General Settings > Feature Flags",
    adminOnly: true
  },

  // ── Session Settings > Timeouts ─────────────────────────────────────
  {
    id: "content-user-timeout",
    label: "User Inactivity Timeout",
    path: "/settings/sessions#timeouts",
    icon: "lucide:timer",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Session Settings > Timeouts",
    adminOnly: true
  },
  {
    id: "content-guest-timeout",
    label: "Guest Timeout",
    path: "/settings/sessions#timeouts",
    icon: "lucide:timer",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Session Settings > Timeouts",
    adminOnly: true
  },

  // ── Session Settings > Persistence & Cookies ────────────────────────
  {
    id: "content-remember-me",
    label: "Remember Me Duration",
    path: "/settings/sessions#persistence-cookies",
    icon: "lucide:cookie",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Session Settings > Persistence & Cookies",
    adminOnly: true
  },
  {
    id: "content-auto-extend",
    label: "Auto-Extend on Activity",
    path: "/settings/sessions#persistence-cookies",
    icon: "lucide:refresh-cw",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Session Settings > Persistence & Cookies",
    adminOnly: true
  },

  // ── Session Settings > Concurrency ──────────────────────────────────
  {
    id: "content-multiple-sessions",
    label: "Allow Multiple Sessions",
    path: "/settings/sessions#concurrency",
    icon: "lucide:users",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Session Settings > Concurrency",
    adminOnly: true
  },

  // ── Session Settings > Session Operations ───────────────────────────
  {
    id: "content-invalidate-sessions",
    label: "Invalidate Non-Admin Sessions",
    path: "/settings/sessions#session-operations",
    icon: "lucide:shield-alert",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Session Settings > Session Operations",
    adminOnly: true
  },

  // ── Security Settings > Disallowed Usernames ────────────────────────
  {
    id: "content-disallowed-usernames",
    label: "Disallowed Usernames",
    path: "/settings/security#disallowed-usernames",
    icon: "lucide:user-x",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Disallowed Usernames",
    adminOnly: true
  },

  // ── Security Settings > Banned IPs ──────────────────────────────────
  {
    id: "content-banned-ips",
    label: "Banned IPs",
    path: "/settings/security#banned-ips",
    icon: "lucide:shield-ban",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Banned IPs",
    adminOnly: true
  },

  // ── Security Settings > Network Enforcement ─────────────────────────
  {
    id: "content-network-enforcement",
    label: "Network Enforcement",
    path: "/settings/security#network-enforcement",
    icon: "lucide:shield-alert",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Network Enforcement",
    adminOnly: true
  },

  // ── Registration Settings > Activation ──────────────────────────────
  {
    id: "content-activation-mode",
    label: "Account Activation Mode",
    path: "/settings/registration#activation",
    icon: "lucide:user-check",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Activation",
    adminOnly: true
  },

  // ── Registration Settings > Quotas ──────────────────────────────────
  {
    id: "content-default-quota",
    label: "Default Disk Quota",
    path: "/settings/registration#quotas",
    icon: "lucide:hard-drive",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Quotas",
    adminOnly: true
  },

  // ── Registration Settings > Default Group ───────────────────────────
  {
    id: "content-default-group",
    label: "Default Registration Group",
    path: "/settings/registration#default-group",
    icon: "lucide:users",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Default Group",
    adminOnly: true
  },

  // ── Registration Settings > Username Policy ─────────────────────────
  {
    id: "content-username-min-length",
    label: "Username Minimum Length",
    path: "/settings/registration#username-policy",
    icon: "lucide:text-cursor-input",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Username Policy",
    adminOnly: true
  },
  {
    id: "content-username-max-length",
    label: "Username Maximum Length",
    path: "/settings/registration#username-policy",
    icon: "lucide:text-cursor-input",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Username Policy",
    adminOnly: true
  },
  {
    id: "content-force-lowercase",
    label: "Force Lowercase Usernames",
    path: "/settings/registration#username-policy",
    icon: "lucide:case-lower",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Username Policy",
    adminOnly: true
  },
  {
    id: "content-username-regex",
    label: "Username Regex Pattern",
    path: "/settings/registration#username-policy",
    icon: "lucide:regex",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Username Policy",
    adminOnly: true
  },

  // ── Registration Settings > Password Policy ─────────────────────────
  {
    id: "content-password-min-length",
    label: "Password Minimum Length",
    path: "/settings/registration#password-policy",
    icon: "lucide:key",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Password Policy",
    adminOnly: true
  },
  {
    id: "content-password-max-length",
    label: "Password Maximum Length",
    path: "/settings/registration#password-policy",
    icon: "lucide:key",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Password Policy",
    adminOnly: true
  },

  // ── Registration Settings > Welcome Email ───────────────────────────
  {
    id: "content-welcome-email",
    label: "Send Welcome Email",
    path: "/settings/registration#welcome-email",
    icon: "lucide:mail-check",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Registration Settings > Welcome Email",
    adminOnly: true
  },

  // ── Security Settings > Firewall Rules Tab ────────────────────────────
  {
    id: "content-firewall-origin-filter",
    label: "Firewall Rule Origin Filter",
    path: "/settings/security?tab=firewall",
    icon: "lucide:filter",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Firewall Rules",
    adminOnly: true
  },
  {
    id: "content-firewall-bulk-delete",
    label: "Bulk Delete Firewall Rules",
    path: "/settings/security?tab=firewall",
    icon: "lucide:trash-2",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Firewall Rules",
    adminOnly: true
  },

  // ── Security Settings > Geo-Lock Tab ──────────────────────────────────
  {
    id: "content-geo-lock-add-rule",
    label: "Add Geo-Lock Rule",
    path: "/settings/security?tab=geo-lock",
    icon: "lucide:plus",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Geo-Lock",
    adminOnly: true
  },
  {
    id: "content-geo-lock-violations",
    label: "Geo-Lock Violations",
    path: "/settings/security?tab=geo-lock",
    icon: "lucide:alert-triangle",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Geo-Lock",
    adminOnly: true
  },

  // ── Security Settings > Geo-Block Tab ─────────────────────────────────
  {
    id: "content-geo-block-countries",
    label: "Blocked Countries List",
    path: "/settings/security?tab=geo-block",
    icon: "lucide:map",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Geo-Block",
    adminOnly: true
  },
  {
    id: "content-geo-block-enforcement",
    label: "Geo-Block Enforcement",
    path: "/settings/security?tab=geo-block",
    icon: "lucide:shield",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Geo-Block",
    adminOnly: true
  },
  {
    id: "content-geo-block-ip-test",
    label: "Geo-Block IP Test",
    path: "/settings/security?tab=geo-block",
    icon: "lucide:search",
    sectionId: "settings",
    sectionLabel: "Settings",
    groupLabel: "Security Settings > Geo-Block",
    adminOnly: true
  },
];
